// Shared carrier-comparison rules so the homepage widget and the quote page
// label options identically. No "AI" scoring: options are ranked on price,
// speed, and a balanced rule that favours our own service.

// The rate platform's TAT field is rich text, so it can carry markup. Our
// delivery line is a single compact row, and a <ul> would wrench the card
// apart - so render it as plain text. (It is sanitised server-side too, this
// is about layout, not safety.)
export const tatToText = (tat) => {
  if (!tat) return '';
  return String(tat)
    .replace(/<\s*(li|br|\/p|\/div|\/li)\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

// Lower-bound transit days taken from the platform's TAT, or null when it
// sends none. Null means "we do not know", not "slow" - callers must not
// substitute a guess, or the badge would make a claim the data cannot support.
export const carrierMinDays = (quote) => {
  const match = tatToText(quote?.tat).match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

export const isOwnService = (quote) => {
  const c = (quote?.carrier_name || '').toLowerCase();
  return c.includes('sendmyparcel') || c.includes('xfas');
};

// Expects quotes already sorted cheapest-first.
// Returns { bestPriceIdx, fastestIdx, recommendedIdx }.
export const computeQuoteHighlights = (sortedQuotes = []) => {
  if (!sortedQuotes.length) {
    return { bestPriceIdx: -1, fastestIdx: -1, recommendedIdx: -1 };
  }

  // "Fastest" is a comparison, so it needs a turnaround for every option. With
  // any of them unknown we cannot say which is quickest, so the badge is simply
  // withheld until the platform carries TAT for all providers.
  const allDays = sortedQuotes.map(carrierMinDays);
  const comparable = allDays.every((d) => d !== null);
  // Cheapest-first ordering means the first hit also wins speed ties on price.
  const fastestIdx = comparable
    ? allDays.reduce((best, d, i) => (best === -1 || d < allDays[best] ? i : best), -1)
    : -1;

  // Balanced: our own service by default; a faster carrier only takes
  // preference when it costs no more than 10% above ours.
  const ownIdx = sortedQuotes.findIndex(isOwnService);
  let recommendedIdx = ownIdx === -1 ? 0 : ownIdx;
  if (ownIdx !== -1) {
    const ownCost = sortedQuotes[ownIdx].total_cost;
    const ownDays = allDays[ownIdx];
    // Only hand our slot to a rival we can actually prove is faster.
    const challenger = ownDays === null ? -1 : sortedQuotes.findIndex(
      (q, i) => i !== ownIdx && allDays[i] !== null && allDays[i] < ownDays && q.total_cost <= ownCost * 1.1
    );
    if (challenger !== -1) recommendedIdx = challenger;
  }

  return { bestPriceIdx: 0, fastestIdx, recommendedIdx };
};

export const badgesForIndex = (index, { bestPriceIdx, fastestIdx, recommendedIdx }) => {
  const badges = [];
  if (index === bestPriceIdx) badges.push({ label: 'Best price', cls: 'bg-[#F17221]' });
  if (index === fastestIdx) badges.push({ label: 'Fastest delivery', cls: 'bg-green-600' });
  if (index === recommendedIdx) badges.push({ label: 'Recommended', cls: 'bg-[#0A1128]' });
  return badges;
};

export const fmtINR = (n) => Math.round(n || 0).toLocaleString('en-IN');

// India Post arrives product-coded ("India Post - EMS", "India Post - ITPS").
// Customers do not know those codes, so the card names the service the way
// India Post sells it over the counter. The carrier_name itself is never
// rewritten - it is the key the booking is priced and reconciled on.
const INDIA_POST_LABELS = [
  { test: /itps|epacket|tracked/i, label: 'India Post Tracked Packet' },
  { test: /air\s*parcel|fgn_air/i, label: 'India Post Air Parcel' },
  { test: /ems|speed\s*post|sp_merch|sp_doc/i, label: 'India Post Speed Post' },
];

export const carrierDisplayName = (carrierName) => {
  if (!carrierName) return '';
  if (!/india\s*post/i.test(carrierName)) return carrierName;
  const hit = INDIA_POST_LABELS.find((n) => n.test.test(carrierName));
  return hit ? hit.label : 'India Post';
};

export const carrierLogo = (carrierName) => {
  if (!carrierName) return null;
  const key = carrierName.toLowerCase().replace(/\s+/g, '');
  const map = {
    indiapost: '/assets/images/carriers/indiapost-v2.svg',
    sendmyparcel: '/assets/images/carriers/sendmyparcel.png',
    xfas: '/assets/images/whitebglogo.jpeg',
    arame: '/assets/images/carriers/arame.png',
    bluedart: '/assets/images/carriers/bluedart.png',
    dhl: '/assets/images/carriers/dhl.png',
    dtdc: '/assets/images/carriers/dtdc.png',
    fedex: '/assets/images/carriers/fedex.png',
    ups: '/assets/images/carriers/ups.png',
  };
  if (map[key]) return map[key];
  for (const [k, logo] of Object.entries(map)) {
    if (key.includes(k) || k.includes(key)) return logo;
  }
  return null;
};
